import type { CaptureEvent, EnvironmentSnapshot } from '@dreamteam/chat-protocol';
import { describeElement, isWidgetElement } from './dom.js';

export interface CaptureOptions {
  network?: boolean;
  clicks?: boolean;
  navigation?: boolean;
  console?: boolean;
  errors?: boolean;
  /** Записывать тела запросов/ответов (JSON/текст), обрезанные до bodyLimit. */
  bodies?: boolean;
  bodyLimit?: number;
  maxEvents?: number;
  /** Сколько истории прикладывать к первому сообщению, мс. */
  maxAgeMs?: number;
  /** URL, которые не записываются (сам сервер чата, аналитика…). */
  ignoreUrls?: Array<string | RegExp>;
  /** Дополнительные ключи JSON, значения которых вырезаются. */
  redactKeys?: string[];
  /** always — события копятся всегда (в памяти вкладки); manual — только пока пользователь включил запись. */
  mode?: 'always' | 'manual';
}

const DEFAULTS: Required<Omit<CaptureOptions, 'ignoreUrls' | 'redactKeys'>> = {
  network: true,
  clicks: true,
  navigation: true,
  console: true,
  errors: true,
  bodies: true,
  bodyLimit: 4000,
  maxEvents: 500,
  maxAgeMs: 15 * 60_000,
  mode: 'always',
};

const SENSITIVE = /pass(word)?|secret|token|auth|cookie|session|api[-_]?key|card|cvv|cvc|pin|snils|passport/i;
const SAFE_RESPONSE_HEADERS = ['content-type', 'x-request-id', 'x-trace-id', 'traceparent', 'x-correlation-id', 'x-variant', 'x-dreamteam-variant'];

export function redactJsonString(body: string, extraKeys: string[] = [], limit = 4000): { text: string; truncated: boolean } {
  let text = body;
  try {
    const obj = JSON.parse(body);
    const extra = new Set(extraKeys.map((k) => k.toLowerCase()));
    const walk = (v: any, depth: number): any => {
      if (depth > 8) return '[depth]';
      if (Array.isArray(v)) return v.slice(0, 50).map((x) => walk(x, depth + 1));
      if (v && typeof v === 'object') {
        const out: Record<string, unknown> = {};
        for (const [k, val] of Object.entries(v)) {
          out[k] = SENSITIVE.test(k) || extra.has(k.toLowerCase()) ? '[redacted]' : walk(val, depth + 1);
        }
        return out;
      }
      return v;
    };
    text = JSON.stringify(walk(obj, 0));
  } catch {
    // не JSON — вырезаем очевидные токены
    text = body.replace(/(Bearer\s+)[\w.-]+/gi, '$1[redacted]').replace(/\beyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]{8,}\b/g, '[jwt]');
  }
  const truncated = text.length > limit;
  return { text: truncated ? text.slice(0, limit) + '…' : text, truncated };
}

function redactHeaders(h: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(h)) out[k] = SENSITIVE.test(k) ? '[redacted]' : v.slice(0, 200);
  return out;
}

function interactiveAncestor(el: Element): Element {
  return el.closest('button, a, [role="button"], [role="link"], [role="menuitem"], [role="tab"], input, select, textarea, label, summary') ?? el;
}

function stringifyArg(a: unknown): string {
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  if (typeof a === 'string') return a;
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

/**
 * Рекордер контекста браузера: переходы, клики, сеть (fetch/XHR), ошибки и console.error/warn.
 * Хранит события в кольцевом буфере в памяти вкладки; наружу они уходят только вместе
 * с сообщением пользователя и только при его согласии.
 */
export class CaptureRecorder {
  private opts: Required<Omit<CaptureOptions, 'ignoreUrls' | 'redactKeys'>> & { ignoreUrls: Array<string | RegExp>; redactKeys: string[] };
  private buffer: CaptureEvent[] = [];
  private dropped = 0;
  private cursor = 0; // абсолютный индекс первого ещё не отправленного события
  private total = 0;
  private uninstallers: Array<() => void> = [];
  private lastUrl = '';
  installed = false;
  /** Пишем ли события сейчас. В режиме manual — только после start(). */
  enabled: boolean;
  /** Текущая сессия записи по кнопке. */
  session: { startedAt: number; stoppedAt: number | null } | null = null;

  constructor(options: CaptureOptions = {}, private win: Window & typeof globalThis = window as any) {
    this.opts = { ...DEFAULTS, ...options, ignoreUrls: [...(options.ignoreUrls ?? [])], redactKeys: options.redactKeys ?? [] } as any;
    this.enabled = options.mode !== 'manual';
  }

  get mode(): 'always' | 'manual' {
    return this.opts.mode === 'manual' ? 'manual' : 'always';
  }

  /** Начать запись действий по кнопке: буфер очищается, чтобы в заявку попало только показанное. */
  start() {
    this.buffer = [];
    this.dropped = 0;
    this.cursor = this.total;
    this.session = { startedAt: Date.now(), stoppedAt: null };
    this.enabled = true;
    this.nav('load');
  }

  stop() {
    if (this.session && this.session.stoppedAt == null) this.session.stoppedAt = Date.now();
    if (this.mode === 'manual') this.enabled = false;
  }

  get recording() {
    return !!this.session && this.session.stoppedAt == null;
  }

  /** Забрать итог сессии записи (и забыть её). */
  takeSession() {
    const s = this.session;
    this.session = null;
    return s && s.stoppedAt != null ? { startedAt: s.startedAt, stoppedAt: s.stoppedAt } : null;
  }

  ignoreUrl(u: string | RegExp) {
    this.opts.ignoreUrls.push(u);
  }

  push(e: CaptureEvent) {
    if (!this.enabled) return;
    this.buffer.push(e);
    this.total++;
    if (this.buffer.length > this.opts.maxEvents) {
      this.buffer.shift();
      this.dropped++;
    }
  }

  /** События с момента предыдущего drain (для первого сообщения — не старше maxAgeMs). */
  drain(now = Date.now()): { events: CaptureEvent[]; dropped: number } {
    const firstIndex = this.total - this.buffer.length;
    const start = Math.max(0, this.cursor - firstIndex);
    const lostSinceCursor = Math.max(0, firstIndex - this.cursor);
    const events = this.buffer.slice(start).filter((e) => now - e.t <= this.opts.maxAgeMs);
    this.cursor = this.total;
    return { events, dropped: lostSinceCursor };
  }

  /** Посмотреть, что будет приложено, без сдвига курсора. */
  peek(now = Date.now()): CaptureEvent[] {
    const firstIndex = this.total - this.buffer.length;
    return this.buffer.slice(Math.max(0, this.cursor - firstIndex)).filter((e) => now - e.t <= this.opts.maxAgeMs);
  }

  environment(appState?: Record<string, unknown>): EnvironmentSnapshot {
    const w = this.win;
    return {
      url: w.location.href,
      title: w.document.title,
      referrer: w.document.referrer,
      userAgent: w.navigator.userAgent,
      language: w.navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      viewport: { width: w.innerWidth, height: w.innerHeight, dpr: w.devicePixelRatio || 1 },
      screen: { width: w.screen?.width ?? 0, height: w.screen?.height ?? 0 },
      appState,
    };
  }

  private ignored(url: string) {
    return this.opts.ignoreUrls.some((p) => (typeof p === 'string' ? url.startsWith(p) : p.test(url)));
  }

  install() {
    if (this.installed) return;
    this.installed = true;
    const o = this.opts;
    if (o.navigation) this.installNavigation();
    if (o.clicks) this.installClicks();
    if (o.network) {
      this.installFetch();
      this.installXhr();
    }
    if (o.errors) this.installErrors();
    if (o.console) this.installConsole();
  }

  uninstall() {
    for (const u of this.uninstallers.reverse()) u();
    this.uninstallers = [];
    this.installed = false;
  }

  /* ---------------- navigation ---------------- */

  private nav(via: 'load' | 'push' | 'replace' | 'pop' | 'hash') {
    const url = this.win.location.href;
    if (url === this.lastUrl && via !== 'load') return;
    this.push({ t: Date.now(), type: 'nav', url, title: this.win.document.title, from: this.lastUrl || undefined, via });
    this.lastUrl = url;
  }

  private installNavigation() {
    const h = this.win.history;
    const origPush = h.pushState;
    const origReplace = h.replaceState;
    const self = this;
    h.pushState = function (this: History, ...args: any[]) {
      const r = origPush.apply(this, args as any);
      self.nav('push');
      return r;
    } as any;
    h.replaceState = function (this: History, ...args: any[]) {
      const r = origReplace.apply(this, args as any);
      self.nav('replace');
      return r;
    } as any;
    const onPop = () => this.nav('pop');
    const onHash = () => this.nav('hash');
    this.win.addEventListener('popstate', onPop);
    this.win.addEventListener('hashchange', onHash);
    this.nav('load');
    this.uninstallers.push(() => {
      h.pushState = origPush;
      h.replaceState = origReplace;
      this.win.removeEventListener('popstate', onPop);
      this.win.removeEventListener('hashchange', onHash);
    });
  }

  /* ---------------- clicks ---------------- */

  private installClicks() {
    const onClick = (ev: MouseEvent) => {
      const raw = ev.composedPath?.()[0] ?? ev.target;
      if (!(raw instanceof Element) || isWidgetElement(raw)) return;
      const el = interactiveAncestor(raw);
      const { rect, ...target } = describeElement(el);
      this.push({ t: Date.now(), type: 'click', url: this.win.location.href, x: Math.round(ev.clientX), y: Math.round(ev.clientY), target: { ...target, rect } });
    };
    this.win.document.addEventListener('click', onClick, true);
    this.uninstallers.push(() => this.win.document.removeEventListener('click', onClick, true));
  }

  /* ---------------- network ---------------- */

  private installFetch() {
    const w = this.win;
    const orig = w.fetch;
    if (!orig) return;
    const self = this;
    w.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const started = Date.now();
      const req = input instanceof Request ? input : null;
      const url = String(req ? req.url : input instanceof URL ? input.href : input);
      const method = (init?.method ?? req?.method ?? 'GET').toUpperCase();
      if (self.ignored(url)) return orig.call(w, input as any, init);
      const reqHeaders: Record<string, string> = {};
      try {
        new Headers(init?.headers ?? req?.headers).forEach((v, k) => (reqHeaders[k] = v));
      } catch {
        /* noop */
      }
      let requestBody: string | undefined;
      if (self.opts.bodies && typeof init?.body === 'string') requestBody = redactJsonString(init.body, self.opts.redactKeys, self.opts.bodyLimit).text;
      try {
        const res = await orig.call(w, input as any, init);
        const base = {
          t: started, type: 'net' as const, transport: 'fetch' as const, method, url: res.url || url, status: res.status, ok: res.ok,
          durationMs: Date.now() - started, requestHeaders: redactHeaders(reqHeaders), requestBody,
          responseHeaders: pickHeaders(res.headers),
        };
        const ct = res.headers.get('content-type') ?? '';
        if (self.opts.bodies && /json|text|xml|problem/.test(ct) && !res.bodyUsed) {
          res
            .clone()
            .text()
            .then((txt) => {
              const r = redactJsonString(txt, self.opts.redactKeys, self.opts.bodyLimit);
              self.push({ ...base, responseBody: r.text, truncated: r.truncated });
            })
            .catch(() => self.push(base));
        } else {
          self.push(base);
        }
        return res;
      } catch (err) {
        self.push({
          t: started, type: 'net', transport: 'fetch', method, url, status: 0, ok: false, durationMs: Date.now() - started,
          requestHeaders: redactHeaders(reqHeaders), requestBody, error: String((err as Error)?.message ?? err),
        });
        throw err;
      }
    } as typeof fetch;
    this.uninstallers.push(() => (w.fetch = orig));
  }

  private installXhr() {
    const X = (this.win as any).XMLHttpRequest as typeof XMLHttpRequest | undefined;
    if (!X) return;
    const proto = X.prototype;
    const origOpen = proto.open;
    const origSend = proto.send;
    const origSet = proto.setRequestHeader;
    const self = this;
    type Meta = { method: string; url: string; headers: Record<string, string>; started: number };
    const metas = new WeakMap<XMLHttpRequest, Meta>();
    proto.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: any[]) {
      metas.set(this, { method: String(method).toUpperCase(), url: String(url), headers: {}, started: 0 });
      return (origOpen as any).call(this, method, url, ...rest);
    } as any;
    proto.setRequestHeader = function (this: XMLHttpRequest, k: string, v: string) {
      const m = metas.get(this);
      if (m) m.headers[k.toLowerCase()] = v;
      return origSet.call(this, k, v);
    };
    proto.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      const m = metas.get(this);
      if (m && !self.ignored(m.url)) {
        m.started = Date.now();
        const requestBody = self.opts.bodies && typeof body === 'string' ? redactJsonString(body, self.opts.redactKeys, self.opts.bodyLimit).text : undefined;
        this.addEventListener('loadend', () => {
          let responseBody: string | undefined;
          let truncated = false;
          try {
            if (self.opts.bodies && (this.responseType === '' || this.responseType === 'text')) {
              const r = redactJsonString(this.responseText, self.opts.redactKeys, self.opts.bodyLimit);
              responseBody = r.text;
              truncated = r.truncated;
            } else if (self.opts.bodies && this.responseType === 'json') {
              const r = redactJsonString(JSON.stringify(this.response), self.opts.redactKeys, self.opts.bodyLimit);
              responseBody = r.text;
              truncated = r.truncated;
            }
          } catch {
            /* noop */
          }
          const ct = this.getResponseHeader('content-type');
          self.push({
            t: m.started, type: 'net', transport: 'xhr', method: m.method, url: this.responseURL || m.url, status: this.status,
            ok: this.status >= 200 && this.status < 400, durationMs: Date.now() - m.started, requestHeaders: redactHeaders(m.headers),
            requestBody, responseHeaders: ct ? { 'content-type': ct } : undefined, responseBody, truncated,
            error: this.status === 0 ? 'network error / aborted' : undefined,
          });
        });
      }
      return origSend.call(this, body as any);
    };
    this.uninstallers.push(() => {
      proto.open = origOpen;
      proto.send = origSend;
      proto.setRequestHeader = origSet;
    });
  }

  /* ---------------- errors & console ---------------- */

  private installErrors() {
    const onError = (e: ErrorEvent) =>
      this.push({ t: Date.now(), type: 'error', message: e.message, source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined, stack: e.error?.stack?.slice(0, 2000) });
    const onRejection = (e: PromiseRejectionEvent) =>
      this.push({ t: Date.now(), type: 'error', message: `Unhandled rejection: ${stringifyArg(e.reason).slice(0, 500)}`, stack: e.reason?.stack?.slice(0, 2000) });
    this.win.addEventListener('error', onError);
    this.win.addEventListener('unhandledrejection', onRejection);
    this.uninstallers.push(() => {
      this.win.removeEventListener('error', onError);
      this.win.removeEventListener('unhandledrejection', onRejection);
    });
  }

  private installConsole() {
    const c = this.win.console;
    for (const level of ['error', 'warn'] as const) {
      const orig = c[level];
      c[level] = (...args: unknown[]) => {
        this.push({ t: Date.now(), type: 'console', level, message: args.map(stringifyArg).join(' ').slice(0, 1000) });
        orig.apply(c, args);
      };
      this.uninstallers.push(() => (c[level] = orig));
    }
  }
}

function pickHeaders(h: Headers): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const k of SAFE_RESPONSE_HEADERS) {
    const v = h.get(k);
    if (v) out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}
